import { useCallback, useEffect, useRef, useState } from 'react';
import { getApiError } from '../../services/api';

export type RequestState = 'loading' | 'error' | 'ready';

/**
 * Loads `load()` whenever `key` changes. `reload` refetches without clearing the data.
 * `key` stands for everything `load` reads, so it must change when those inputs do; a null key waits (stays loading).
 * `missing` is set (together with state 'error') when the API answered 404: retrying cannot help.
 */
export function useRequest<T>(key: string | null, load: () => Promise<T>) {
    const [data, setData] = useState<T | null>(null);
    const [state, setState] = useState<RequestState>('loading');
    const [missing, setMissing] = useState(false);
    const [nonce, setNonce] = useState(0);
    const loadRef = useRef(load);
    loadRef.current = load;

    useEffect(() => {
        if (key === null) return;
        let cancelled = false;
        loadRef
            .current()
            .then((result) => {
                if (cancelled) return;
                setData(result);
                setMissing(false);
                setState('ready');
            })
            .catch((e: unknown) => {
                if (cancelled) return;
                setMissing(getApiError(e).status === 404);
                setState('error');
            });
        return () => {
            cancelled = true;
        };
    }, [key, nonce]);

    useEffect(() => {
        setState('loading');
        setData(null);
    }, [key]);

    const reload = useCallback(() => setNonce((n) => n + 1), []);
    const retry = useCallback(() => {
        setState('loading');
        setNonce((n) => n + 1);
    }, []);
    return { data, setData, state, missing, reload, retry };
}
